import { useId, useState, type FormEvent, type HTMLAttributes, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cx } from './cx'
import { Icon } from './Icon'
import { Button } from './Button'
import { HelpTip, isExplanation } from './HelpTip'

/** Wrapper di pagina: colonna con gap 24. Sostituisce `container-fluid py-3` e i div nudi. */
export function Page({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cx('md-page', className)} />
}

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode
  subtitle?: ReactNode | undefined
  icon?: string | undefined
  /** Numero di elementi: mostrato accanto al titolo, non al posto del titolo. */
  count?: number | undefined
  actions?: ReactNode | undefined
  back?: { to: string; label: string } | undefined
  tabs?: ReactNode | undefined
}

export function PageHeader({ title, subtitle, icon, count, actions, back, tabs, className, ...rest }: PageHeaderProps) {
  return (
    <header {...rest} className={cx('md-page-header', className)}>
      {back ? (
        <Link to={back.to} className="md-page-header__back md-state">
          <Icon name="arrow_back" size={18} />
          {back.label}
        </Link>
      ) : null}
      <div className="md-page-header__row">
        <div className="md-page-header__titles">
          <h1 className="md-page-header__title">
            {icon ? <Icon name={icon} size={24} className="md-page-header__icon" /> : null}
            <span>{title}</span>
            {typeof count === 'number' ? <span className="md-page-header__count md-num">{count}</span> : null}
            {isExplanation(subtitle) ? <HelpTip>{subtitle}</HelpTip> : null}
          </h1>
          {subtitle && !isExplanation(subtitle) ? <p className="md-page-header__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="md-page-header__actions">{actions}</div> : null}
      </div>
      {tabs ? <div className="md-page-header__tabs">{tabs}</div> : null}
    </header>
  )
}

export interface FilterBarProps extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit' | 'onReset'> {
  onSubmit?: (() => void) | undefined
  onReset?: (() => void) | undefined
  /** Numero di filtri attivi: mostrato nel bottone "Filtri (n)" dei viewport stretti. */
  activeCount?: number | undefined
  actions?: ReactNode | undefined
  /** Operazione in corso: disabilita Applica e Reimposta. */
  busy?: boolean | undefined
  /** Filtri meno usati: restano chiusi dietro "Altri filtri" (aperti da soli se uno è in uso). */
  more?: ReactNode | undefined
  /** Quanti dei filtri in `more` sono in uso. */
  moreActive?: number | undefined
  children: ReactNode
}

/** Barra filtri. Sotto 600px i campi si raccolgono dietro un bottone "Filtri (n)". */
export function FilterBar({ onSubmit, onReset, activeCount, actions, busy, more, moreActive, children, className, ...rest }: FilterBarProps) {
  const [open, setOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const showMore = moreOpen || (moreActive ?? 0) > 0
  const regionId = useId()
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSubmit?.()
  }
  return (
    <form
      role="search"
      {...rest}
      onSubmit={submit}
      onReset={(e) => {
        e.preventDefault()
        onReset?.()
      }}
      className={cx('md-filter-bar', open && 'md-filter-bar--open', className)}
    >
      <Button
        variant="tonal"
        size="sm"
        icon="filter_list"
        className="md-filter-bar__toggle"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((v) => !v)}
      >
        {typeof activeCount === 'number' ? `Filtri (${activeCount})` : 'Filtri'}
      </Button>
      <div id={regionId} className="md-filter-bar__body">
        <div className="md-filter-bar__fields">{children}</div>
        <div className="md-filter-bar__buttons">
          {more ? (
            <Button type="button" variant="text" icon={showMore ? 'expand_less' : 'tune'} aria-expanded={showMore}
              onClick={() => setMoreOpen(!showMore)}>
              {moreActive ? `Altri filtri (${moreActive})` : 'Altri filtri'}
            </Button>
          ) : null}
          {onSubmit ? (
            <Button type="submit" variant="tonal" icon="search" disabled={busy}>
              Applica
            </Button>
          ) : null}
          {onReset ? (
            <Button type="reset" variant="text" disabled={busy}>
              Reimposta
            </Button>
          ) : null}
          {actions}
        </div>
        {more && showMore ? <div className="md-filter-bar__fields md-filter-bar__more">{more}</div> : null}
      </div>
    </form>
  )
}
